import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Gift, Users, Award, Calendar, TrendingUp, Star, Percent, CheckCircle, XCircle, Clock, Building2 } from 'lucide-react';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { ProjectDetail } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { MembershipFormUtils } from '../../utils/membershipCommon';
import { getMembershipList, MembershipListItem } from '../../utils/membership';
import { getProjectCode } from '../../utils/auth';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';

const getStatusConfig = (status: string) => {
  switch (status.toUpperCase()) {
    case 'ACTIVE':
      return { label: '운영중', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    case 'INACTIVE':
      return { label: '비활성', color: 'bg-gray-100 text-gray-700', icon: Clock };
    case 'PENDING':
      return { label: '대기중', color: 'bg-amber-100 text-amber-700', icon: Clock };
    case 'TERMINATED':
    case 'ENDED':
      return { label: '종료', color: 'bg-red-100 text-red-700', icon: XCircle };
    default:
      return { label: status, color: 'bg-gray-100 text-gray-700', icon: Clock };
  }
};

const getRewardTypeLabel = (type: string) => {
  switch (type.toUpperCase()) {
    case 'STAMP': return '스탬프';
    case 'POINT': return '포인트';
    case 'NONE': return '없음';
    default: return type;
  }
};

const getRewardTypeColor = (type: string) => {
  switch (type.toUpperCase()) {
    case 'STAMP': return 'bg-purple-100 text-purple-700';
    case 'POINT': return 'bg-blue-100 text-blue-700';
    case 'NONE': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const formatValidPeriod = (membership: MembershipListItem) => {
  if (membership.validType === 1 && membership.validAt) {
    return formatDateTimeFromApi(membership.validAt) || membership.validAt;
  }
  if (membership.validType === 0 && membership.validPeriod) {
    return `${membership.validPeriod}일`;
  }
  return undefined;
};

const getPointRewardTargetLabel = (target?: string) => {
  switch (target?.toUpperCase()) {
    case 'PAYMENT': return '결제금액';
    case 'PRODUCT': return '상품';
    default: return target || '-';
  }
};

// 멤버십 카드 컴포넌트
const MembershipCard: React.FC<{ membership: MembershipListItem; onClick: () => void }> = ({ membership, onClick }) => {
  const statusConfig = getStatusConfig(membership.status);
  const StatusIcon = statusConfig.icon;
  const rewardType = membership.rewardType?.toUpperCase() || 'NONE';
  const validPeriodLabel = formatValidPeriod(membership);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden group"
    >
      {/* 헤더 */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
              {membership.title}
            </h3>
            <p className="text-sm text-gray-500 font-mono mt-1">{membership.code}</p>
          </div>
          <div className="ml-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* 리워드 타입 및 특성 */}
        <div className="flex flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${getRewardTypeColor(rewardType)}`}>
            <Gift className="w-3 h-3" />
            {getRewardTypeLabel(rewardType)}
          </span>
          {membership.paidMembership === 1 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700">
              <Star className="w-3 h-3" />
              유료
            </span>
          )}
          {membership.membershipLevel > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-rose-100 text-rose-700">
              <Award className="w-3 h-3" />
              등급제
            </span>
          )}
        </div>
      </div>

      {/* 통계 */}
      <div className="p-5 bg-gray-50">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Users className="w-4 h-4" />
              회원 수
            </div>
            <p className="text-xl font-bold text-gray-900">-</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Building2 className="w-4 h-4" />
              브랜드
            </div>
            <p className="text-xl font-bold text-gray-900">
              {membership.brandCount}개
            </p>
          </div>
        </div>
      </div>

      {/* 리워드 상세 정보 */}
      <div className="p-5 space-y-3">
        {(rewardType === 'STAMP' || rewardType === 'POINT') && membership.rewardTitle && (
          <div
            className={`rounded-lg p-3 border ${
              rewardType === 'STAMP'
                ? 'bg-purple-50 border-purple-100'
                : 'bg-blue-50 border-blue-100'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className={`w-6 h-6 rounded flex items-center justify-center ${
                  rewardType === 'STAMP' ? 'bg-purple-500' : 'bg-blue-500'
                }`}
              >
                {rewardType === 'STAMP' ? (
                  <Gift className="w-4 h-4 text-white" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-white" />
                )}
              </div>
              <span
                className={`font-semibold text-sm ${
                  rewardType === 'STAMP' ? 'text-purple-900' : 'text-blue-900'
                }`}
              >
                {membership.rewardTitle}
              </span>
            </div>
            <div className="space-y-1">
              {rewardType === 'STAMP' && membership.stampSetCount ? (
                <div className="flex justify-between text-xs">
                  <span className="text-purple-700">스탬프/세트</span>
                  <span className="font-medium text-purple-900">{membership.stampSetCount}개</span>
                </div>
              ) : null}
              {rewardType === 'POINT' && membership.pointRatio != null ? (
                <div className="flex justify-between text-xs">
                  <span className="text-blue-700">적립률</span>
                  <span className="font-medium text-blue-900 flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    {membership.pointRatio}%
                  </span>
                </div>
              ) : null}
              {validPeriodLabel && (
                <div className="flex justify-between text-xs">
                  <span className={rewardType === 'STAMP' ? 'text-purple-700' : 'text-blue-700'}>
                    유효기간
                  </span>
                  <span
                    className={`font-medium ${
                      rewardType === 'STAMP' ? 'text-purple-900' : 'text-blue-900'
                    }`}
                  >
                    {validPeriodLabel}
                  </span>
                </div>
              )}
              {rewardType === 'POINT' && membership.pointRewardTarget && (
                <div className="flex justify-between text-xs">
                  <span className="text-blue-700">적립 대상</span>
                  <span className="font-medium text-blue-900">
                    {getPointRewardTargetLabel(membership.pointRewardTarget)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 운영 기간 */}
        <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-200">
          <Calendar className="w-4 h-4" />
          <span>{formatDateTimeFromApi(membership.startAt) || membership.startAt || ''}</span>
          <span>~</span>
          <span>{membership.endAt ? formatDateTimeFromApi(membership.endAt) || membership.endAt : '무기한'}</span>
        </div>
      </div>
    </div>
  );
};

// 메인 컴포넌트
const MembershipList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('membership-list');

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Data state
  const [memberships, setMemberships] = useState<MembershipListItem[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [rewardTypeFilter, setRewardTypeFilter] = useState<string>('all');
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  
  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Check if screen is mobile size
  MembershipFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = MembershipFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) return;
      try {
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) return;
        const response = await getBrandListFlat(projectCode);
        if (response?.brands) {
          setBrands(response.brands);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
      }
    };
    if (projectDetail) fetchBrands();
  }, [projectDetail]);

  // 멤버십 목록 로드
  useEffect(() => {
    const loadMembershipList = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        
        const projectCode = getProjectCode();
        if (!projectCode) {
          setLoadError('프로젝트 코드를 찾을 수 없습니다.');
          setIsLoading(false);
          return;
        }

        const result = await getMembershipList(projectCode, 0, 0, {
          brandCode: selectedBrand !== 'all' ? selectedBrand : undefined,
          status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
          rewardType: rewardTypeFilter !== 'all' ? rewardTypeFilter.toUpperCase() : undefined,
        });
        setMemberships(result.memberships);
      } catch (error: any) {
        console.error('멤버십 목록 로드 실패:', error);
        setLoadError(error.message || '멤버십 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      loadMembershipList();
    }
  }, [projectDetail, selectedBrand, statusFilter, rewardTypeFilter]);

  // 통계 계산 (회원 수는 API 미제공으로 - 표시)
  const stats = {
    total: memberships.length,
    active: memberships.filter((m) => m.status.toUpperCase() === 'ACTIVE').length,
    paidCount: memberships.filter((m) => m.paidMembership === 1).length,
  };

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">멤버십 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <XCircle className="w-12 h-12 mx-auto mb-2" />
            <p className="font-semibold">오류가 발생했습니다</p>
          </div>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar 
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="멤버십 목록"
          subtitle="멤버십 프로그램을 관리하고 운영합니다"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Actions - ProductList와 동일: lg 기준 한 줄/두 줄, 왼쪽 필터 flex-1, 오른쪽 버튼 w-full sm:w-auto */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 브랜드</option>
                    {brands.map((brand) => (
                      <option key={brand.code} value={brand.code}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">운영중</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="PENDING">대기중</option>
                    <option value="TERMINATED">종료</option>
                  </select>
                  <select
                    value={rewardTypeFilter}
                    onChange={(e) => setRewardTypeFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 리워드</option>
                    <option value="NONE">없음</option>
                    <option value="STAMP">스탬프</option>
                    <option value="POINT">포인트</option>
                  </select>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigate('/memberships/create')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    새 멤버십
                  </button>
                </div>
              </div>
            </div>

            {/* 통계 위젯 */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-indigo-300 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">전체 멤버십</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-green-300 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">운영중</p>
                <p className="text-xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">전체 회원</p>
                <p className="text-xl font-bold text-gray-900">-</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-amber-300 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">유료 멤버십</p>
                <p className="text-xl font-bold text-gray-900">{stats.paidCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 멤버십 그리드 */}
        {memberships.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {memberships.map((membership) => (
              <MembershipCard
                key={membership.code}
                membership={membership}
                onClick={() => navigate(`/memberships/${encodeURIComponent(membership.code)}/update`)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              등록된 멤버십이 없습니다
            </p>
            <button
                onClick={() => navigate('/memberships/create')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                첫 멤버십 만들기
              </button>
          </div>
        )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MembershipList;